import React from 'react';
import { Globe } from '@phosphor-icons/react';
import {
  OrientationLandscapeIcon,
  OrientationPortraitIcon,
} from '../icons/OrientationIcon';
import { Switch } from '../ui/switch';
import { SegmentedControl } from '../ui/SegmentedControl';
import { Checkbox } from '../ui/checkbox';
import { IconToggleGroup } from '../ui/IconToggleGroup';
import { ColorSwatchGroup } from '../ui/ColorSwatchGroup';
import { useTranslation } from '../../i18n/I18nContext';
import './PrintControls.css';

/**
 * Settings sidebar for print templates (issue #1021).
 *
 * Sticky vertical column on the right of the print page. On `@media print`
 * the sidebar collapses to `display: none` so it never leaks into output.
 *
 * Controls:
 *  1. Language     — fr / en / de / lu (default = browser locale)
 *                    Globe icon как label, 2-letter сегменты
 *  2. Mode         — `full` | `qr-only`
 *  3. Orientation  — `horizontal` | `vertical` (с rectangle-icons)
 *  4. Mirror       — `off` | `on` — mirror-flipped output для наклеек
 *                    изнутри стекла (info icon + tooltip)
 *  5. Color        — `black` | `white` — white = PDF-only, прячет Format
 *  6. Format       — `pdf` | `svg` — скрывается при color=white
 *
 * `LabeledSegmented` = label-строка + DS-атом `ui/SegmentedControl`
 * (`variant="subtle"`). Атом поддерживает opt.icon (lucide компонент) — рендерит
 * иконку вместо текста, label попадает в aria-label для a11y.
 * label === null + iconLabel — заменяет текст header'а на иконку.
 */
const LANGUAGE_OPTIONS = [
  { value: 'fr', label: 'FR', title: 'Français' },
  { value: 'en', label: 'EN', title: 'English' },
  { value: 'de', label: 'DE', title: 'Deutsch' },
  { value: 'lu', label: 'LU', title: 'Lëtzebuergesch' },
];

// Mode/orientation options строятся в render-функции через t() — labels
// и aria-labels должны реактивно перерендериваться на смену языка.
// Static константа здесь не подходит.

const COLOR_OPTIONS = [
  { value: 'white', bg: '#fff', textColor: '#000', ariaLabel: 'White (black text on white background)' },
  { value: 'black', bg: '#000', textColor: '#fff', ariaLabel: 'Black (white text on black background)' },
];

const FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'svg', label: 'SVG' },
];

const PrintControls = ({
  language,
  onLanguageChange,
  // showLanguage=false → скрываем Language toggle. Используется для
  // used-VIN темплейтов (Andys), где i18n integration не сделана —
  // показывать toggle без эффекта вводило бы оператора в заблуждение.
  showLanguage = true,
  mode,
  onModeChange,
  // showMode=false → скрываем Type segmented control. Включается когда
  // страница открыта с `?mode=catalog-qr` из b2b-ui Inventory (#1022) —
  // оператор уже принял решение «catalog QR, не per-car», переключение
  // обратно на Car info нелогично (seed-машина для catalog-qr —
  // артефакт URL'а, выбора авто оператор не делал).
  showMode = true,
  // showCatalogQr=false → убираем опцию "Catalog QR" из Type. Это salon-фича
  // (#1022, Autodis): для used-VIN (Andys) salon welcome пока не показываем. (#1064)
  showCatalogQr = true,
  // showOrientation=false → скрываем Orientation control. Для used-VIN
  // (Andys) листовка landscape-only: вертикальной версии нет, контрол
  // убран чтобы оператор не выбрал портрет (который кропает). (#1064)
  showOrientation = true,
  orientation,
  onOrientationChange,
  // showMirror/showColor=false → скрываем Mirror и Color. Для used-VIN
  // (Andys) панель упрощена до Format + скачивание (Маша 26.06, #1064). (#1064)
  showMirror = true,
  mirror,
  onMirrorChange,
  showColor = true,
  color,
  onColorChange,
  format,
  onFormatChange,
  cutMarks,
  onCutMarksChange,
  children,
}) => {
  const { t } = useTranslation();

  // Build localized option lists inside render — лейблы реактивно
  // обновляются при смене языка через useTranslation.
  const modeOptions = [
    { value: 'full', label: t('print.controls.typeOptions.full') },
    { value: 'qr-only', label: t('print.controls.typeOptions.qrOnly') },
    ...(showCatalogQr
      ? [{ value: 'catalog-qr', label: t('print.controls.typeOptions.catalogQr') }]
      : []),
  ];
  const orientationOptions = [
    { value: 'horizontal', icon: OrientationLandscapeIcon, ariaLabel: t('print.controls.orientationHorizontal') },
    { value: 'vertical', icon: OrientationPortraitIcon, ariaLabel: t('print.controls.orientationVertical') },
  ];

  return (
    <aside className="print-controls-bar" aria-label={t('print.controls.title', 'Print settings')}>
      <div className="print-controls">
        {showLanguage ? (
          <div className="print-controls__group print-controls__group--inline">
            <div className="print-controls__label-row">
              <Globe
                className="print-controls__label-icon"
                size={16}
                weight="regular"
                aria-hidden="true"
              />
              <span className="print-controls__label" id="print-language-label">{t('print.controls.language')}</span>
            </div>
            <select
              aria-labelledby="print-language-label"
              className="print-controls__select"
              value={language}
              onChange={(e) => onLanguageChange?.(e.target.value)}
            >
              {LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} title={opt.title}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {showMode ? (
          <LabeledSegmented
            label={t('print.controls.type')}
            value={mode}
            onChange={onModeChange}
            options={modeOptions}
          />
        ) : null}

        {mode === 'qr-only' || !showOrientation ? null : (
          <div className="print-controls__group">
            <div className="print-controls__label-row">
              <span className="print-controls__label">{t('print.controls.orientation')}</span>
            </div>
            <IconToggleGroup
              value={orientation}
              onChange={onOrientationChange}
              options={orientationOptions}
              ariaLabel={t('print.controls.orientation')}
            />
          </div>
        )}

        {showMirror ? (
          <SwitchRow
            label={t('print.controls.mirror')}
            checked={mirror === 'on'}
            onChange={(next) => onMirrorChange?.(next ? 'on' : 'off')}
          />
        ) : null}

        {mode === 'qr-only' ? (
          <div className="print-controls__group">
            <Checkbox
              id="print-cut-marks"
              checked={!!cutMarks}
              onCheckedChange={onCutMarksChange}
              label={t('print.controls.cutMarks')}
            />
          </div>
        ) : null}

        {showColor ? (
          <div className="print-controls__group">
            <div className="print-controls__label-row">
              <span className="print-controls__label">{t('print.controls.color')}</span>
            </div>
            <ColorSwatchGroup
              value={color}
              onChange={onColorChange}
              options={COLOR_OPTIONS}
              ariaLabel={t('print.controls.color')}
            />
          </div>
        ) : null}

        <LabeledSegmented
          label={t('print.controls.format')}
          value={format}
          onChange={onFormatChange}
          options={FORMAT_OPTIONS}
        />
      </div>

      {children ? (
        <div className="print-controls__actions">{children}</div>
      ) : null}
    </aside>
  );
};

/**
 * Segmented radio control. Опция может иметь:
 *   - `label` (string) — текст в кнопке
 *   - `icon` (React component) — рендерится вместо текста, в `aria-label`
 *     попадает `ariaLabel` или `value`
 *   - `ariaLabel` (string) — текст для screen-reader даже при icon-only
 *
 * Header label-row показывает либо `<label>`-текст, либо `iconLabel`
 * (lucide компонент) — например Globe для языка.
 */
const LabeledSegmented = ({
  label,
  iconLabel: IconLabel,
  value,
  onChange,
  options,
}) => (
  <div className="print-controls__group">
    <div className="print-controls__label-row">
      {IconLabel ? (
        <IconLabel
          className="print-controls__label-icon"
          size={16}
          weight="regular"
          aria-hidden="true"
        />
      ) : null}
      <span className="print-controls__label">{label}</span>
    </div>
    <SegmentedControl
      variant="subtle"
      value={value}
      onChange={onChange}
      options={options}
      ariaLabel={label}
    />
  </div>
);

/**
 * Inline row: label + Switch toggle справа. Используется когда контрол
 * бинарный (on/off) и заслуживает компактнее формы чем сегментированный
 * двух-кнопочный сегментированный контрол. Switch берётся из ../ui/switch — он уже
 * compliant с Shadow DOM (см. switch.css комментарий об issue с @property
 * в Tailwind v4).
 */
const SwitchRow = ({ label, checked, onChange }) => (
  <div className="print-controls__group print-controls__group--inline">
    <div className="print-controls__label-row">
      <span className="print-controls__label">{label}</span>
    </div>
    <Switch checked={checked} onChange={onChange} />
  </div>
);

export default PrintControls;
